import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, serial, timestamp, boolean, json, unique, doublePrecision } from "drizzle-orm/pg-core";
import { createInsertSchema, createSelectSchema } from "drizzle-zod";
import { z } from "zod";

export const users = pgTable("users", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  password: text("password"), // nullable: comptes passwordless (magic link)
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  phoneNumber: text("phone_number"), // nullable: le tel n'est plus demandé au signup
  isVerified: boolean("is_verified").notNull().default(false),
  isPhoneVerified: boolean("is_phone_verified").notNull().default(false),
  isSuspended: boolean("is_suspended").notNull().default(false),
  suspendedUntil: timestamp("suspended_until"),
  suspensionReason: text("suspension_reason"),
  language: text("language").notNull().default("en"),
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  acceptsMarketingEmails: boolean("accepts_marketing_emails").notNull().default(true),
  lastLoginAt: timestamp("last_login_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const deletedAccounts = pgTable("deleted_accounts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  deletedAt: timestamp("deleted_at").defaultNow().notNull(),
});


export const pendingRegistrations = pgTable("pending_registrations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  firstName: text("first_name").notNull(),
  lastName: text("last_name").notNull(),
  phoneNumber: text("phone_number").notNull(),
  verificationCode: text("verification_code").notNull(),
  verificationAttempts: integer("verification_attempts").notNull().default(0),
  lastVerificationAttempt: timestamp("last_verification_attempt"),
  expiresAt: timestamp("expires_at").notNull(),
  language: text("language").notNull().default("en"),
  utmSource: text("utm_source"),
  utmMedium: text("utm_medium"),
  utmCampaign: text("utm_campaign"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const registrationLogs = pgTable("registration_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  ipAddress: text("ip_address"),
  email: text("email"),
  phoneNumber: text("phone_number"),
  firstName: text("first_name"),
  lastName: text("last_name"),
  isSuccess: boolean("is_success").notNull().default(false),
  failureReason: text("failure_reason"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const verificationCodes = pgTable("verification_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id),
  pendingRegistrationId: varchar("pending_registration_id").references(() => pendingRegistrations.id),
  code: text("code").notNull(),
  type: text("type").notNull(), // 'email', 'sms', 'password_reset'
  phoneNumber: text("phone_number"), // Only for SMS verification
  expiresAt: timestamp("expires_at").notNull(),
  isUsed: boolean("is_used").notNull().default(false),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const players = pgTable("players", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id),
  name: text("name").notNull(),
  eloRating: integer("elo_rating").notNull().default(1500),
  matchesWon: integer("matches_won").notNull().default(0),
  matchesLost: integer("matches_lost").notNull().default(0),
  matchesPlayed: integer("matches_played").notNull().default(0),
  isPremium: boolean("is_premium").notNull().default(false),
  premiumType: text("premium_type"), // 'monthly', 'yearly', 'lifetime'
  premiumStartDate: timestamp("premium_start_date"),
  premiumEndDate: timestamp("premium_end_date"),
  fftRank: integer("fft_rank"),
  fftEvolution: integer("fft_evolution"),
  fftTournaments: integer("fft_tournaments"),
  fftPoints: integer("fft_points"),
  fftLastUpdated: timestamp("fft_last_updated"),
  fftLicence: text("fft_licence"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const premiumSubscriptions = pgTable("premium_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  playerId: varchar("player_id").references(() => players.id).notNull(),
  subscriptionType: text("subscription_type").notNull(), // 'monthly', 'yearly', 'lifetime'
  status: text("status").notNull().default("active"), // 'active', 'cancelled', 'expired', 'pending'
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date"), // null for lifetime subscriptions
  price: integer("price").notNull(), // price in cents
  paymentMethod: text("payment_method"), // 'stripe', 'paypal', 'manual', etc.
  paymentId: text("payment_id"), // external payment system ID
  isAutoRenew: boolean("is_auto_renew").notNull().default(true),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const matches = pgTable("matches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  clubId: varchar("club_id").references(() => clubs.id),
  createdByPlayerId: varchar("created_by_player_id").references(() => players.id),
  teamAPlayer1Id: varchar("team_a_player1_id").references(() => players.id).notNull(),
  teamAPlayer2Id: varchar("team_a_player2_id").references(() => players.id).notNull(),
  teamBPlayer1Id: varchar("team_b_player1_id").references(() => players.id).notNull(),
  teamBPlayer2Id: varchar("team_b_player2_id").references(() => players.id).notNull(),
  teamAScore: json("team_a_score").$type<number[]>().notNull().default([]),
  teamBScore: json("team_b_score").$type<number[]>().notNull().default([]),
  teamAGameScore: integer("team_a_game_score").notNull().default(0),
  teamBGameScore: integer("team_b_game_score").notNull().default(0),
  currentSet: integer("current_set").notNull().default(1),
  isActive: boolean("is_active").notNull().default(true),
  isCompleted: boolean("is_completed").notNull().default(false),
  isConfirmed: boolean("is_confirmed").notNull().default(false),
  teamAConfirmedBy: varchar("team_a_confirmed_by").references(() => players.id),
  teamAPlayer2ConfirmedBy: varchar("team_a_player2_confirmed_by").references(() => players.id),
  teamBConfirmedBy: varchar("team_b_confirmed_by").references(() => players.id),
  teamBPlayer2ConfirmedBy: varchar("team_b_player2_confirmed_by").references(() => players.id),
  requiredConfirmers: json("required_confirmers").$type<string[]>().notNull().default([]),
  winnerId: varchar("winner_id"), // null for ties, team identifier for completed matches
  matchType: text("match_type").notNull().default("single_set_6"), // best_of_3, best_of_5, single_set, single_set_6, single_set_9
  startTime: timestamp("start_time").defaultNow().notNull(),
  endTime: timestamp("end_time"),
  duration: integer("duration"), // in seconds
  history: json("history").$type<any[]>().notNull().default([]),
});

export const clubs = pgTable("clubs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: text("slug").unique(),
  description: text("description"),
  createdById: varchar("created_by_id").references(() => users.id),
  // Validation methods: 'auto', 'standard', 'admin_only'
  validationMethod: text("validation_method").notNull().default("standard"),
  // New Player Validation: 'auto', 'admins'
  newPlayerValidation: text("new_player_validation").notNull().default("auto"),
  // ELO Starting options: 'standard', 'global'
  eloStarting: text("elo_starting").notNull().default("standard"),
  // Official Verified Badge
  isVerified: boolean("is_verified").notNull().default(false),
  // Array of parsed season archives
  archivedSeasons: json("archived_seasons").$type<any[]>().notNull().default([]),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const clubMemberships = pgTable("club_memberships", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  clubId: varchar("club_id").references(() => clubs.id).notNull(),
  playerId: varchar("player_id").references(() => players.id).notNull(),
  isAdmin: boolean("is_admin").notNull().default(false),
  clubEloRating: integer("club_elo_rating").notNull().default(1500),
  clubMatchesWon: integer("club_matches_won").notNull().default(0),
  clubMatchesLost: integer("club_matches_lost").notNull().default(0),
  clubMatchesPlayed: integer("club_matches_played").notNull().default(0),
  joinedAt: timestamp("joined_at").defaultNow().notNull(),
  // Status: 'active', 'pending'
  status: text("status").notNull().default("active"),
}, (table) => ({
  uniqueClubPlayer: unique("unique_club_player").on(table.clubId, table.playerId),
}));

export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  playerId: varchar("player_id").references(() => players.id).notNull(),
  title: text("title").notNull(),
  description: text("description").notNull(),
  type: text("type").notNull(), // 'match', 'club', 'premium', 'system', 'achievement'
  isRead: boolean("is_read").notNull().default(false),
  metadata: json("metadata"), // Additional data like matchId, clubId, etc.
  createdAt: timestamp("created_at").defaultNow().notNull(),
});


export const revokedTokens = pgTable("revoked_tokens", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tokenHash: text("token_hash").notNull(),
  userId: varchar("user_id").references(() => users.id).notNull(),
  revokedAt: timestamp("revoked_at").defaultNow().notNull(),
  revokedBy: varchar("revoked_by").references(() => users.id),
  reason: text("reason"),
  expiresAt: timestamp("expires_at").notNull(),
});

export const alertSettings = pgTable("alert_settings", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  weeklyMatchesLimit: integer("weekly_matches_limit").notNull().default(5),
  monthlyMatchesLimit: integer("monthly_matches_limit").notNull().default(20),
  bagelsLimit: integer("bagels_limit").notNull().default(10),
  bestBroWinRate: integer("best_bro_win_rate").notNull().default(90),
  bestBroMatches: integer("best_bro_matches").notNull().default(10),
  emailAlertsEnabled: boolean("email_alerts_enabled").notNull().default(false),
  emailRecipient: text("email_recipient"),
  alertCooldownHours: integer("alert_cooldown_hours").notNull().default(48),
  // New: Limit for winning/losing streak before triggering an alert
  spreeLimit: integer("spree_limit").notNull().default(10),
  autoLifetimePremium: boolean("auto_lifetime_premium").notNull().default(false), // New: Auto-enable lifetime premium for new users
  minVersion: text("min_version").notNull().default("1.0.0"), // Minimum required app version
  whatsNewVersion: text("whats_new_version").notNull().default(""),
  whatsNewContentFr: text("whats_new_content_fr").notNull().default(""),
  whatsNewContentEn: text("whats_new_content_en").notNull().default(""),
  whatsNewContentEs: text("whats_new_content_es").notNull().default(""),
  fftMaxRankH: integer("fft_max_rank_h").notNull().default(120000),
  fftMaxRankF: integer("fft_max_rank_f").notNull().default(20000),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});


// Notification schemas
export const insertNotificationSchema = createInsertSchema(notifications);
export const PadelNotification = createSelectSchema(notifications);
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type PadelNotificationType = z.infer<typeof PadelNotification>;

// Notification type union
export type NotificationType = 'match' | 'club' | 'premium' | 'system' | 'achievement';

export const matchResults = pgTable("match_results", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  matchId: varchar("match_id").references(() => matches.id).notNull(),
  playerId: varchar("player_id").references(() => players.id).notNull(),
  eloChange: integer("elo_change").notNull(),
  newEloRating: integer("new_elo_rating").notNull(),
  clubEloChange: integer("club_elo_change"),
  newClubEloRating: integer("new_club_elo_rating"),
  isWinner: boolean("is_winner").notNull(),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertPendingRegistrationSchema = createInsertSchema(pendingRegistrations).omit({
  id: true,
  createdAt: true,
});

export const insertUserSchema = createInsertSchema(users, {
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters").optional(),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  phoneNumber: z.string().min(10).optional(),
}).omit({
  id: true,
  createdAt: true,
  isVerified: true,
  isPhoneVerified: true,
  isSuspended: true,
  suspendedUntil: true,
  suspensionReason: true,
});

export const insertVerificationCodeSchema = createInsertSchema(verificationCodes).omit({
  id: true,
  createdAt: true,
  isUsed: true,
});

export const insertPlayerSchema = createInsertSchema(players).omit({
  id: true,
  createdAt: true,
  eloRating: true,
  matchesWon: true,
  matchesLost: true,
  matchesPlayed: true,
  // userId: true, // Allow setting userId during creation
});

export const insertMatchSchema = createInsertSchema(matches).omit({
  id: true,
  startTime: true,
  endTime: true,
  duration: true,
  currentSet: true,
  teamAScore: true,
  teamBScore: true,
  teamAGameScore: true,
  teamBGameScore: true,
  isActive: true,
  isCompleted: true,
  winnerId: true,
  createdByPlayerId: true, // Will be set server-side based on authenticated user
  history: true,
});

export const insertClubSchema = createInsertSchema(clubs).extend({
  name: z.string().min(1, "Name is required").max(25, "Le nom du club ne peut pas dépasser 25 caractères")
}).omit({
  id: true,
  createdAt: true,
  createdById: true, // Will be set server-side based on authenticated user
});

export const insertClubMembershipSchema = createInsertSchema(clubMemberships).omit({
  id: true,
  joinedAt: true,
});

export const insertMatchResultSchema = createInsertSchema(matchResults).omit({
  id: true,
  createdAt: true,
});

export const insertPremiumSubscriptionSchema = createInsertSchema(premiumSubscriptions).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type InsertPendingRegistration = z.infer<typeof insertPendingRegistrationSchema>;
export type PendingRegistration = typeof pendingRegistrations.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;
export type InsertVerificationCode = z.infer<typeof insertVerificationCodeSchema>;
export type VerificationCode = typeof verificationCodes.$inferSelect;
export type InsertPlayer = z.infer<typeof insertPlayerSchema>;
export type Player = typeof players.$inferSelect;
export type InsertClub = z.infer<typeof insertClubSchema>;
export type Club = typeof clubs.$inferSelect;
export type InsertClubMembership = z.infer<typeof insertClubMembershipSchema>;
export type ClubMembership = typeof clubMemberships.$inferSelect;
export type InsertMatch = z.infer<typeof insertMatchSchema>;
export type Match = typeof matches.$inferSelect;
export type InsertMatchResult = z.infer<typeof insertMatchResultSchema>;
export type MatchResult = typeof matchResults.$inferSelect;
export type InsertPremiumSubscription = z.infer<typeof insertPremiumSubscriptionSchema>;
export type PremiumSubscription = typeof premiumSubscriptions.$inferSelect;
export type RevokedToken = typeof revokedTokens.$inferSelect;

export type MatchWithPlayers = Match & {
  teamAPlayer1: Player;
  teamAPlayer2: Player;
  teamBPlayer1: Player;
  teamBPlayer2: Player;
};

export type MatchWithPlayersAndClub = MatchWithPlayers & {
  club?: Club;
};

export type MatchWithPlayersClubAndResults = MatchWithPlayersAndClub & {
  matchResults: MatchResult[];
};

export type PlayerRanking = Player & {
  rank: number;
  winRate: number;
  firstName?: string | null;
  lastName?: string | null;
};

export type ClubWithMembership = Club & {
  memberCount: number;
  isMember?: boolean;
  isAdmin?: boolean;
  status?: string;
  createdByName?: string;
  currentMembers?: ClubMember[];
};

export type ClubMember = Player & {
  joinedAt: Date;
  isAdmin: boolean;
  clubEloRating: number;
  clubMatchesWon: number;
  clubMatchesLost: number;
  clubMatchesPlayed: number;
  status: string;
};

export type ClubRanking = PlayerRanking & {
  isAdmin: boolean;
  clubEloRating: number;
  clubMatchesWon: number;
  clubMatchesLost: number;
  clubMatchesPlayed: number;
  clubWinRate: number;
};

export type PlayerWithPremium = Player & {
  premiumSubscriptions?: PremiumSubscription[];
  activePremiumSubscription?: PremiumSubscription;
};

// Device Tokens Table (matches existing SQL definition)
export const deviceTokens = pgTable("device_tokens", {
  id: varchar("id", { length: 36 }).primaryKey(),
  playerId: varchar("player_id", { length: 36 }).references(() => players.id).notNull(),
  token: text("token").notNull().unique(),
  platform: varchar("platform", { length: 10 }).notNull(), // 'web', 'android', 'ios'
  isActive: boolean("is_active").default(true),
  createdAt: timestamp("created_at").defaultNow(),
  lastUsed: timestamp("last_used").defaultNow(),
});

// Player Statistics Table (matches existing SQL usage)
export const playerStatistics = pgTable("player_statistics", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  playerId: varchar("player_id", { length: 36 }).references(() => players.id).notNull().unique(),
  createdAt: timestamp("created_at").defaultNow(),
  globalMatchesPlayed: integer("global_matches_played").default(0),
  globalMatchesWon: integer("global_matches_won").default(0),
  globalWinRate: doublePrecision("global_win_rate").default(0), // Using integer/real as number in TS

  leftMatchesPlayed: integer("left_matches_played").default(0),
  leftMatchesWon: integer("left_matches_won").default(0),
  leftWinRate: doublePrecision("left_win_rate").default(0),

  rightMatchesPlayed: integer("right_matches_played").default(0),
  rightMatchesWon: integer("right_matches_won").default(0),
  rightWinRate: doublePrecision("right_win_rate").default(0),

  leftBestPartnerId: varchar("left_best_partner_id"),
  leftBestPartnerName: text("left_best_partner_name"),
  leftBestPartnerMatches: integer("left_best_partner_matches").default(0),
  leftBestPartnerWins: integer("left_best_partner_wins").default(0),
  leftBestPartnerWinRate: doublePrecision("left_best_partner_win_rate").default(0),

  rightBestPartnerId: varchar("right_best_partner_id"),
  rightBestPartnerName: text("right_best_partner_name"),
  rightBestPartnerMatches: integer("right_best_partner_matches").default(0),
  rightBestPartnerWins: integer("right_best_partner_wins").default(0),
  rightBestPartnerWinRate: doublePrecision("right_best_partner_win_rate").default(0),

  currentSpree: integer("current_spree").default(0),
  bestSpree: integer("best_spree").default(0),
  worstSpree: integer("worst_spree").default(0),

  gamesPerWeek: integer("games_per_week").default(0),
  gamesPerMonth: integer("games_per_month").default(0),
  bagels: integer("bagels").default(0),
  bagelsReceived: integer("bagels_received").default(0),
  allTimeBestPartnerId: varchar("all_time_best_partner_id", { length: 36 }),
  allTimeBestPartnerName: text("all_time_best_partner_name"),
  allTimeBestPartnerMatches: integer("all_time_best_partner_matches").default(0),
  allTimeBestPartnerWins: integer("all_time_best_partner_wins").default(0),
  allTimeBestPartnerWinRate: doublePrecision("all_time_best_partner_win_rate").default(0),

  lastAlertSentAt: timestamp("last_alert_sent_at"),
  lastUpdated: timestamp("last_updated").defaultNow(),
  streakHistory: json("streak_history").$type<number[]>().default([]),
});

export const insertDeviceTokenSchema = createInsertSchema(deviceTokens);
export const insertPlayerStatisticsSchema = createInsertSchema(playerStatistics);
export type DeviceToken = typeof deviceTokens.$inferSelect;
export type PlayerStatistic = typeof playerStatistics.$inferSelect;

export const insertRegistrationLogSchema = createInsertSchema(registrationLogs);
export type InsertRegistrationLog = z.infer<typeof insertRegistrationLogSchema>;
export type RegistrationLog = typeof registrationLogs.$inferSelect;

// Monthly Player Statistics (Snapshot)
export const monthlyPlayerStats = pgTable("monthly_player_stats", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  playerId: varchar("player_id", { length: 36 }).references(() => players.id).notNull(),
  month: varchar("month", { length: 7 }).notNull(), // Format: "YYYY-MM"
  globalRank: integer("global_rank").default(0),
  eloRating: integer("elo_rating").notNull(),
  matchesPlayed: integer("matches_played").default(0),
  matchesWon: integer("matches_won").default(0),
  winRate: doublePrecision("win_rate").default(0),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  uniqueMonthlyPlayer: unique("unique_monthly_player").on(table.playerId, table.month),
}));

// Monthly Club Rankings (Snapshot)
export const monthlyClubRankings = pgTable("monthly_club_rankings", {
  id: varchar("id", { length: 36 }).primaryKey().default(sql`gen_random_uuid()`),
  playerId: varchar("player_id", { length: 36 }).references(() => players.id).notNull(),
  clubId: varchar("club_id", { length: 36 }).references(() => clubs.id).notNull(),
  month: varchar("month", { length: 7 }).notNull(), // Format: "YYYY-MM"
  clubRank: integer("club_rank").default(0),
  clubEloRating: integer("club_elo_rating").notNull(),
  matchesPlayed: integer("matches_played").default(0), // matches played in this club this month
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  uniqueMonthlyClubPlayer: unique("unique_monthly_club_player").on(table.playerId, table.clubId, table.month),
}));

export const insertMonthlyPlayerStatsSchema = createInsertSchema(monthlyPlayerStats);
export type MonthlyPlayerStat = typeof monthlyPlayerStats.$inferSelect;

export const insertMonthlyClubRankingSchema = createInsertSchema(monthlyClubRankings);
export type MonthlyClubRanking = typeof monthlyClubRankings.$inferSelect;

export const loginLogs = pgTable("login_logs", {
  id: serial("id").primaryKey(),
  userId: varchar("user_id", { length: 36 }).references(() => users.id, { onDelete: "cascade" }).notNull(),
  loggedAt: timestamp("logged_at", { withTimezone: true }).defaultNow().notNull(),
  platform: text("platform"),
});

export const websiteVisits = pgTable("website_visits", {
  id: serial("id").primaryKey(),
  path: text("path").notNull(),
  referrer: text("referrer"),
  userAgent: text("user_agent"),
  ipHash: varchar("ip_hash", { length: 64 }),
  utmSource: text("utm_source"),
  destination: text("destination"), // download page only: ios | android | web
  visitedAt: timestamp("visited_at", { withTimezone: true }).defaultNow().notNull(),
});

export const insertWebsiteVisitSchema = createInsertSchema(websiteVisits).omit({
  id: true,
  visitedAt: true,
});
export type InsertWebsiteVisit = z.infer<typeof insertWebsiteVisitSchema>;
export type WebsiteVisit = typeof websiteVisits.$inferSelect;

// ─── Americano Tournament ────────────────────────────────────────────────────

export const americanoTournaments = pgTable("americano_tournaments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  clubId: varchar("club_id").references(() => clubs.id),
  createdByPlayerId: varchar("created_by_player_id").references(() => players.id).notNull(),
  // 'classic_sets' | 'points_13' | 'points_21' | 'points_24' | 'points_32' | 'timed'
  scoreMode: text("score_mode").notNull().default("points_21"),
  timeLimitMinutes: integer("time_limit_minutes"), // only for scoreMode='timed'
  numberOfCourts: integer("number_of_courts").notNull().default(1),
  // 'setup' | 'in_progress' | 'completed' | 'cancelled'
  status: text("status").notNull().default("setup"),
  currentRound: integer("current_round").notNull().default(0),
  totalRounds: integer("total_rounds").notNull().default(0),
  // Pre-generated round schedule stored as JSON
  roundsSchedule: json("rounds_schedule").$type<AmericanoRoundSchedule[]>().notNull().default([]),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const americanoPlayers = pgTable("americano_players", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tournamentId: varchar("tournament_id").references(() => americanoTournaments.id).notNull(),
  playerId: varchar("player_id").references(() => players.id).notNull(),
  // 'left' | 'right'
  side: text("side").notNull(),
  totalPoints: integer("total_points").notNull().default(0),
  matchesPlayed: integer("matches_played").notNull().default(0),
  finalRank: integer("final_rank"),
  eloChange: integer("elo_change"),
}, (table) => ({
  uniqueTournamentPlayer: unique("unique_americano_tournament_player").on(table.tournamentId, table.playerId),
}));

export const americanoRounds = pgTable("americano_rounds", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tournamentId: varchar("tournament_id").references(() => americanoTournaments.id).notNull(),
  roundNumber: integer("round_number").notNull(),
  // 'pending' | 'in_progress' | 'completed'
  status: text("status").notNull().default("pending"),
  startedAt: timestamp("started_at"),
  completedAt: timestamp("completed_at"),
});

export const americanoRoundMatches = pgTable("americano_round_matches", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tournamentId: varchar("tournament_id").references(() => americanoTournaments.id).notNull(),
  roundId: varchar("round_id").references(() => americanoRounds.id).notNull(),
  courtNumber: integer("court_number").notNull(),
  // Team A: leftPlayer + rightPlayer
  teamALeftPlayerId: varchar("team_a_left_player_id").references(() => players.id).notNull(),
  teamARightPlayerId: varchar("team_a_right_player_id").references(() => players.id).notNull(),
  // Team B: leftPlayer + rightPlayer
  teamBLeftPlayerId: varchar("team_b_left_player_id").references(() => players.id).notNull(),
  teamBRightPlayerId: varchar("team_b_right_player_id").references(() => players.id).notNull(),
  scoreTeamA: integer("score_team_a").notNull().default(0),
  scoreTeamB: integer("score_team_b").notNull().default(0),
  isCompleted: boolean("is_completed").notNull().default(false),
});

// ─── Americano helper types (used in JSON columns) ───────────────────────────

export type AmericanoRoundSchedule = {
  roundNumber: number;
  courts: {
    courtNumber: number;
    teamALeftPlayerId: string;
    teamARightPlayerId: string;
    teamBLeftPlayerId: string;
    teamBRightPlayerId: string;
  }[];
};

// ─── Americano Zod schemas ───────────────────────────────────────────────────

export const insertAmericanoTournamentSchema = createInsertSchema(americanoTournaments).omit({
  id: true,
  createdAt: true,
  startedAt: true,
  completedAt: true,
  currentRound: true,
  totalRounds: true,
  roundsSchedule: true,
  status: true,
  createdByPlayerId: true, // set server-side
}).extend({
  name: z.string().min(1, "Name is required").max(50, "Name too long"),
  numberOfCourts: z.number().int().min(1).max(8),
  scoreMode: z.enum(["classic_sets", "points_13", "points_21", "points_24", "points_32", "timed"]),
  leftPlayerIds: z
    .array(z.string())
    .min(2, "At least 2 left players required")
    .max(16, "Au maximum 16 joueurs par côté"),
  rightPlayerIds: z
    .array(z.string())
    .min(2, "At least 2 right players required")
    .max(16, "Au maximum 16 joueurs par côté"),
}).refine((data) => data.leftPlayerIds.length === data.rightPlayerIds.length, {
  message: "Must have equal number of left and right players",
}).refine((data) => data.leftPlayerIds.length + data.rightPlayerIds.length <= 32, {
  message: "Un tournoi Americano est limité à 32 joueurs",
});

export const updateAmericanoRoundMatchScoreSchema = z.object({
  scoreTeamA: z.number().int().min(0),
  scoreTeamB: z.number().int().min(0),
});

// ─── Americano TypeScript types ──────────────────────────────────────────────

export type InsertAmericanoTournament = z.infer<typeof insertAmericanoTournamentSchema>;
export type AmericanoTournament = typeof americanoTournaments.$inferSelect;
export type AmericanoPlayer = typeof americanoPlayers.$inferSelect;
export type InsertAmericanoPlayer = typeof americanoPlayers.$inferInsert;
export type AmericanoRound = typeof americanoRounds.$inferSelect;
export type InsertAmericanoRound = typeof americanoRounds.$inferInsert;
export type AmericanoRoundMatch = typeof americanoRoundMatches.$inferSelect;
export type InsertAmericanoRoundMatch = typeof americanoRoundMatches.$inferInsert;

export type AmericanoPlayerWithDetails = AmericanoPlayer & {
  player: Player;
};

export type AmericanoRoundMatchWithPlayers = AmericanoRoundMatch & {
  teamALeftPlayer: Player;
  teamARightPlayer: Player;
  teamBLeftPlayer: Player;
  teamBRightPlayer: Player;
};

export type AmericanoRoundWithMatches = AmericanoRound & {
  matches: AmericanoRoundMatchWithPlayers[];
};

export type AmericanoTournamentWithDetails = AmericanoTournament & {
  players: AmericanoPlayerWithDetails[];
  rounds: AmericanoRoundWithMatches[];
  club?: Club;
  createdBy: Player;
};
